/**
 * 图片URL处理工具函数
 * 使用相对路径，通过Vite代理访问后端图片
 * 不依赖特定端口，自动适应环境变化
 */

/**
 * 将业务资源绝对 URL 转为相对路径，走当前站点 / Vite 代理，避免直连 localhost 或远程写死端口导致 ERR_CONNECTION_REFUSED
 * @param {string} url
 * @returns {string}
 */
function toSameOriginUploadsPath(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.trim();
  if (!s.startsWith('http://') && !s.startsWith('https://')) return s;
  try {
    const u = new URL(s);
    const path = `${u.pathname || ''}${u.search || ''}${u.hash || ''}`;
    // 仅重写本应用静态资源与 API 前缀，外链 CDN 仍保留绝对地址
    if (
      path.startsWith('/uploads/')
      || path.startsWith('/api/uploads/')
      || path.startsWith('/static/')
    ) {
      return path.startsWith('/') ? path : `/${path}`;
    }
  } catch {
    /* ignore */
  }
  // 回环主机：任意路径一律改相对
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(s)) {
    return s.replace(/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i, '');
  }
  return s;
}

/**
 * 处理商品图片URL
 * @param {string} imageUrl - 原始图片URL
 * @returns {string|null} - 处理后的图片URL，如果无效则返回null
 */
export const processImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
    return null;
  }

  let processedUrl = imageUrl.trim();

  // 如果是逗号分隔的多个图片URL，取第一个
  if (processedUrl.includes(',')) {
    processedUrl = processedUrl.split(',')[0].trim();
  }

  // 1) data: 直接返回（早退）
  if (String(processedUrl).startsWith('data:')) {
    return processedUrl;
  }

  // 2) 绝对地址中的 /uploads、/api 等改为同源相对路径
  processedUrl = toSameOriginUploadsPath(processedUrl);

  // 3) 已是完整外链（非本应用资源）直接返回
  if (processedUrl.startsWith('http://') || processedUrl.startsWith('https://')) {
    return processedUrl;
  }

  // 4) 相对路径直接返回（Vite 代理）
  if (processedUrl.startsWith('/')) {
    return processedUrl;
  }

  // 5) 其它老路径再按商品目录规则处理
  return `/uploads/products/${processedUrl}`;
};

/**
 * 获取后端服务器基础URL（当前页面域名）
 * @returns {string} - 当前页面域名
 */
export const getBackendBaseUrl = () => {
  return window.location.origin;
};

/**
 * 检查图片URL是否有效
 * @param {string} imageUrl - 图片URL
 * @returns {boolean} - 是否有效
 */
export const isValidImageUrl = (imageUrl) => {
  return processImageUrl(imageUrl) !== null;
};

/**
 * 生成商品图片上传URL
 * @param {string} productId - 商品ID
 * @param {string} imageType - 图片类型（main-image, images, display-images）
 * @returns {string} - 上传URL
 */
export const getProductImageUploadUrl = (productId, imageType) => {
  return `/api/product-images/${productId}/${imageType}`;
};

/**
 * 生成商品图片更新URL
 * @param {string} productId - 商品ID
 * @param {string} imageType - 图片类型
 * @returns {string} - 更新URL
 */
export const getProductImageUpdateUrl = (productId, imageType) => {
  return `/api/product-images/${productId}/${imageType}`;
};

/**
 * 生成商品图片删除URL
 * @param {string} productId - 商品ID
 * @returns {string} - 删除URL
 */
export const getProductImageDeleteUrl = (productId) => {
  return `/api/product-images/${productId}/image`;
};

/**
 * 生成商品所有图片删除URL
 * @param {string} productId - 商品ID
 * @returns {string} - 删除所有图片URL
 */
export const getProductAllImagesDeleteUrl = (productId) => {
  return `/api/product-images/${productId}/all`;
};

/**
 * 解析逗号分隔的图片URL字符串为数组
 * @param {string} imagesString - 逗号分隔的图片URL字符串
 * @returns {string[]} - 图片URL数组
 */
export const parseImagesString = (imagesString) => {
  if (!imagesString || typeof imagesString !== 'string') {
    return [];
  }
  
  return imagesString.split(',')
    .map(url => url.trim())
    .filter(url => url.length > 0);
};

/**
 * 将图片URL数组转换为逗号分隔的字符串
 * @param {string[]} imagesArray - 图片URL数组
 * @returns {string} - 逗号分隔的图片URL字符串
 */
export const convertImagesArrayToString = (imagesArray) => {
  if (!Array.isArray(imagesArray) || imagesArray.length === 0) {
    return '';
  }
  
  return imagesArray
    .filter(url => url && typeof url === 'string' && url.trim().length > 0)
    .map(url => url.trim())
    .join(',');
};

/**
 * 获取图片缩略图URL（用于列表显示）
 * @param {string} imageUrl - 原图片URL
 * @param {number} width - 缩略图宽度（预留参数）
 * @param {number} height - 缩略图高度（预留参数）
 * @returns {string} - 缩略图URL
 */
export const getThumbnailUrl = (imageUrl, width = 150, height = 150) => {
  if (!imageUrl) return '';
  
  // 这里可以添加缩略图生成逻辑
  // 目前直接返回原图，后续可以集成图片处理服务
  // eslint-disable-next-line no-unused-vars
  return processImageUrl(imageUrl);
};

/**
 * 验证图片文件类型
 * @param {File} file - 文件对象
 * @returns {boolean} - 是否为有效的图片文件
 */
export const isValidImageFile = (file) => {
  if (!file) return false;
  
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * 获取图片文件大小（格式化显示）
 * @param {number} bytes - 字节数
 * @returns {string} - 格式化后的文件大小
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
